import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ZodiacRing from "../components/ZodiacRing";
import Zodiac3D from "../components/Zodiac3D";
import TestimonialsSection from "../components/TestimonialsSection";
import { useContent } from "../lib/content";

export default function Landing() {
  const { t } = useContent();
  return (
    <div data-testid="landing-page" className="min-h-screen">
      <Navbar />

      {/* HERO */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 bg-[#06060C]" />
        <div className="absolute inset-0 starfield starfield-drift" />
        {/* 3D wheel as background, SVG wheel in front */}
        <Zodiac3D />
        <ZodiacRing />
        {/* Subtle radial vignette so the headline reads cleanly */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(6,6,12,0.85) 0%, rgba(6,6,12,0.55) 30%, rgba(6,6,12,0.25) 55%, transparent 80%)",
          }}
        />

        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <p className="overline mb-6 rise rise-1">{t("hero_overline")}</p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter leading-[0.95] text-white rise rise-2">
            {(() => {
              const title = t("hero_title", "Conectando con lo sutil");
              const emph = t("hero_title_emphasis", "lo sutil");
              if (emph && title.includes(emph)) {
                const parts = title.split(emph);
                return (<>{parts[0]}<em className="emph-gold">{emph}</em>{parts[1] || ""}</>);
              }
              return title;
            })()}
          </h1>
          <p className="mt-8 text-zinc-400 max-w-xl mx-auto leading-relaxed rise rise-3 text-lg">
            {t("hero_subtitle")}
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4 rise rise-4">
            <Link to="/solicitar" data-testid="hero-cta-primary" className="btn-lilac">
              {t("hero_cta_primary")} ✦
            </Link>
            <Link to="/carta" data-testid="hero-cta-secondary" className="btn-ghost">
              {t("hero_cta_secondary")}
            </Link>
          </div>
        </div>

        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 text-zinc-600 text-xs tracking-[0.3em] uppercase">
          {t("hero_scroll_hint")}
        </div>
      </section>

      {/* HOW IT WORKS — bento */}
      <section className="relative max-w-7xl mx-auto px-6 sm:px-10 py-32">
        <div className="mb-20 max-w-2xl">
          <p className="overline mb-4">{t("features_overline")}</p>
          <h2 className="font-display text-4xl sm:text-5xl font-light tracking-tight text-white">
            {t("features_title")} <em className="emph-gold">{t("features_title_emphasis")}</em>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <Feature className="md:col-span-7" step={t("features_1_step")} title={t("features_1_title")} body={t("features_1_body")} />
          <Feature className="md:col-span-5" step={t("features_2_step")} title={t("features_2_title")} body={t("features_2_body")} />
          <Feature className="md:col-span-4" step={t("features_3_step")} title={t("features_3_title")} body={t("features_3_body")} />
          <Feature className="md:col-span-4" step={t("features_4_step")} title={t("features_4_title")} body={t("features_4_body")} />
          <Feature className="md:col-span-4" step={t("features_5_step")} title={t("features_5_title")} body={t("features_5_body")} />
        </div>
      </section>

      {/* PLANS */}
      <section className="relative max-w-7xl mx-auto px-6 py-32">
        <div className="mb-16 text-center max-w-2xl mx-auto">
          <p className="overline mb-4">{t("plans_overline")}</p>
          <h2 className="font-display text-4xl sm:text-5xl font-light tracking-tight text-white">
            {t("plans_title")}
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <PlanCard kind="free" />
          <PlanCard kind="premium" highlighted />
          <PlanCard kind="session" />
        </div>
      </section>

      {/* TESTIMONIALS — auto-hides when no published items */}
      <TestimonialsSection featuredOnly />

      <Footer />
    </div>
  );
}

function Feature({ className = "", step, title, body }) {
  return (
    <div className={`card-feature ${className}`}>
      <p className="overline mb-3">{step}</p>
      <h3 className="font-display text-2xl sm:text-3xl text-white mb-3">{title}</h3>
      <p className="text-zinc-400 leading-relaxed text-sm">{body}</p>
    </div>
  );
}

function PlanCard({ kind, highlighted = false }) {
  const { t } = useContent();
  const cta = {
    free: { to: "/carta", testid: "plan-free-cta" },
    premium: { to: "/solicitar?plan=premium", testid: "plan-premium-cta" },
    session: { to: "/solicitar?plan=session", testid: "plan-session-cta" },
  }[kind];
  const includesText = t(`plan_${kind}_includes`, "") || "";
  // Prices are only revealed on /solicitar — the homepage stays focused on
  // intention (Free vs Paid Reports vs 1:1). The "free" tier keeps its label.
  const showPrice = kind === "free";
  return (
    <div data-testid={`plan-card-${kind}`} className={`card-feature ${highlighted ? "highlight" : ""} flex flex-col`}>
      {highlighted && (
        <span className="overline absolute top-6 right-6">Recomendado</span>
      )}
      <p className="overline mb-3">{t(`plan_${kind}_subtitle`)}</p>
      <h3 className="font-display text-3xl text-white mb-1">{t(`plan_${kind}_name`)}</h3>
      {showPrice && (
        <p className="font-display text-4xl text-[#C9A25E] my-4">{t(`plan_${kind}_price`)}</p>
      )}
      <p className={`text-zinc-400 text-sm leading-relaxed mb-6 ${showPrice ? "" : "mt-4"}`}>{t(`plan_${kind}_description`)}</p>
      <ul className="space-y-2 mb-8 flex-1">
        {includesText.split("\n").filter(Boolean).map((line, i) => (
          <li key={i} className="text-sm text-zinc-300 flex items-start gap-2">
            <span className="text-[#C9A25E] mt-0.5">✦</span>
            <span>{line}</span>
          </li>
        ))}
      </ul>
      <Link to={cta.to} data-testid={cta.testid} className={highlighted ? "btn-lilac justify-center" : "btn-ghost text-center"}>
        {t(`plan_${kind}_cta`)}
      </Link>
    </div>
  );
}
